/**
 * Serie del digest nel tempo: prezzo obiettivo e allocazione consigliata
 * (F-135).
 *
 * ## Perché non è `ValueTrendChart`
 *
 * Quel grafico confronta **patrimoni**: due serie su ordini di grandezza
 * diversi, «stessi euro per pixel, zeri diversi», ciascuna che parte dalla
 * propria altezza. È la scelta giusta là — si legge quanto ha reso ciascuno
 * *rispetto a dove è partito* — ed è esattamente quella sbagliata qui.
 *
 * ⚠️ **Prezzo obiettivo e prezzo di mercato vanno letti per LIVELLO**, non per
 * pendenza: la domanda è «l'obiettivo è sopra o sotto il prezzo, e di quanto».
 * Con due zeri diversi le due linee partirebbero appaiate per costruzione e un
 * obiettivo sotto il prezzo sembrerebbe sopra. Qui la scala è **una sola**, e
 * le due linee stanno dove stanno.
 *
 * L'altra differenza è l'unità: là sono sempre soldi, qui possono essere
 * percentuali. Il formato arriva dal chiamante (`format`) invece di essere
 * dedotto, così il grafico non deve indovinare cosa sta disegnando.
 *
 * Ciò che invece si **riprende** da `ValueTrendChart`, perché era già stato
 * pagato una volta: l'ascissa è l'**istante** e non l'indice del punto (una
 * settimana ferma e un giorno di salto non possono occupare lo stesso spazio),
 * e il viewBox segue la larghezza misurata, così un'unità SVG è un pixel e non
 * c'è nessuna scala anisotropa che stiri testo e pallini.
 *
 * ⚠️ I punti sono **radi** — un digest per giorno di borsa, con buchi — quindi
 * i pallini contano quanto la linea: senza, due giudizi a dieci giorni di
 * distanza sembrerebbero una rilevazione continua.
 */

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { formatDate } from '@/lib/format';
import { CHART_GUTTERS, dayAtOffset, dayOffset } from './briefing-history';
import './briefing-history.css';

export type HistorySeries = {
  label: string;
  /**
   * `secondary` = tratto più sottile e smorzato: è contesto, non il soggetto.
   * `peer-a`/`peer-b` = due soggetti alla pari (borsa e crypto nello storico del
   * sentiment, F-177): due colori categoriali, e il secondo anche tratteggiato
   * perché l'identità non stia nel solo colore.
   */
  kind: 'primary' | 'secondary' | 'peer-a' | 'peer-b';
  points: ReadonlyArray<{ date: string; value: string | null }>;
};

type Props = {
  series: readonly HistorySeries[];
  /** Come si scrive un valore: il grafico non deve dedurre l'unità. */
  format: (value: string) => string;
  /** Didascalia per la tabella accessibile e per gli screen reader. */
  label: string;
  /**
   * ⚠️ Il dominio dell'ascissa è la **finestra intera**, non l'intervallo dei
   * dati. Tre grafici della stessa pagina che partissero ciascuno dal proprio
   * primo punto non sarebbero confrontabili: lo stesso giorno starebbe a tre
   * ascisse diverse, e il colpo d'occhio verticale — che è il motivo per cui
   * stanno impilati — mentirebbe.
   */
  domain: { from: string; to: string };
  /** La data evidenziata, condivisa con gli altri grafici della pagina. */
  hoveredDate: string | null;
  onHover: (date: string | null) => void;
  /**
   * Scala fissa invece di quella dettata dai dati. Per una misura con estremi
   * propri (lo score da -100 a +100, il Fear & Greed da 0 a 100): un +12 non
   * deve sembrare un massimo solo perché è il valore più alto del mese.
   */
  bounds?: { lower: number; upper: number };
  /** Altezza del disegno; il default è quella dello storico dei giudizi. */
  height?: number;
};

const DEFAULT_HEIGHT = 220;
const FALLBACK_WIDTH = 720;
const TICK_COUNT = 4;
const PADDING_TOP = 16;
const PADDING_BOTTOM = 28;
const PADDING_LEFT = CHART_GUTTERS.left;
const PADDING_RIGHT = CHART_GUTTERS.right;

type Sample = { date: string; value: string; numeric: number; time: number; x: number; y: number };

/** Scala arrotondata "gentile": estremi leggibili invece che dettati dai dati. */
function niceBounds(min: number, max: number): { lower: number; upper: number } {
  if (min === max) {
    const padding = Math.abs(min) * 0.1 || 1;
    return { lower: min - padding, upper: max + padding };
  }
  const span = max - min;
  const step = Math.pow(10, Math.floor(Math.log10(span))) / 2;
  return { lower: Math.floor(min / step) * step, upper: Math.ceil(max / step) * step };
}

/** Lo zero merita una linea più marcata solo se la scala lo attraversa: altrimenti è solo il fondo. */
function lowerBelowZero(ticks: ReadonlyArray<{ value: number }>): boolean {
  return ticks.length > 0 && ticks[0]!.value < 0;
}

/**
 * La larghezza resa dell'elemento, per far coincidere unità SVG e pixel.
 *
 * Senza `ResizeObserver` — jsdom, browser antichi — resta il ripiego: il
 * grafico si disegna comunque, solo con una scala che non segue il contenitore.
 */
function useMeasuredWidth(): [React.RefObject<HTMLDivElement | null>, number] {
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(FALLBACK_WIDTH);

  useEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const measured = entries[0]?.contentRect.width ?? 0;
      if (measured > 0) setWidth(Math.max(240, Math.round(measured)));
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return [ref, width];
}

export function BriefingHistoryChart({ series, format, label, domain, hoveredDate, onHover, bounds, height = DEFAULT_HEIGHT }: Props) {
  const viewHeight = height;
  const titleId = useId();
  const [containerRef, width] = useMeasuredWidth();

  const plotWidth = Math.max(1, width - PADDING_LEFT - PADDING_RIGHT);
  const plotHeight = viewHeight - PADDING_TOP - PADDING_BOTTOM;

  const scene = useMemo(() => {
    const raw = series.map((line) => ({
      ...line,
      samples: line.points
        .map((point) => ({
          date: point.date,
          value: point.value ?? '',
          numeric: point.value === null ? NaN : Number(point.value),
          time: Date.parse(point.date),
        }))
        // ⚠️ Un valore assente NON vale zero: si salta il punto. Uno zero
        // trascinerebbe la scala e disegnerebbe un crollo mai avvenuto.
        .filter((sample) => Number.isFinite(sample.numeric) && Number.isFinite(sample.time)),
    }));

    const all = raw.flatMap((line) => line.samples);
    if (all.length === 0) return null;

    // ⚠️ Sulla finestra intera, non sul primo e ultimo punto: vedi `domain`.
    const xOf = (date: string): number => PADDING_LEFT + dayOffset(date, domain.from, domain.to) * plotWidth;

    // Una scala sola per tutte le serie: è il punto dell'intero componente.
    const values = all.map((sample) => sample.numeric);
    const { lower, upper } = bounds ?? niceBounds(Math.min(...values), Math.max(...values));
    const range = upper - lower || 1;
    const yOf = (value: number): number =>
      PADDING_TOP + plotHeight - ((value - lower) / range) * plotHeight;

    const lines = raw.map((line) => ({
      label: line.label,
      kind: line.kind,
      samples: line.samples.map<Sample>((sample) => ({
        ...sample,
        x: xOf(sample.date),
        y: yOf(sample.numeric),
      })),
    }));
    return {
      lines,
      ticks: Array.from({ length: TICK_COUNT + 1 }, (_, index) => {
        const value = lower + ((upper - lower) * index) / TICK_COUNT;
        return { value, y: yOf(value) };
      }),
    };
  }, [series, plotWidth, plotHeight, domain.from, domain.to, bounds]);

  /** Dal puntatore al giorno: l'evidenziazione vale su tutta la colonna, non solo sul pallino. */
  const dayUnderPointer = (event: React.MouseEvent<SVGSVGElement>): string | null => {
    const box = event.currentTarget.getBoundingClientRect();
    // Le coordinate del viewBox coincidono con i pixel (larghezza misurata),
    // quindi il rapporto è diretto.
    const x = ((event.clientX - box.left) / box.width) * width;
    return dayAtOffset((x - PADDING_LEFT) / plotWidth, domain.from, domain.to);
  };

  if (!scene) {
    return <p className="briefing-history__empty">Nessun dato da disegnare in questo periodo.</p>;
  }

  const hovered = hoveredDate
    ? scene.lines
        .map((line) => ({ label: line.label, sample: line.samples.find((s) => s.date === hoveredDate) }))
        .filter((entry): entry is { label: string; sample: Sample } => entry.sample !== undefined)
    : [];

  return (
    <div className="briefing-history__chart" ref={containerRef}>
      <svg
        viewBox={`0 0 ${width} ${viewHeight}`}
        width="100%"
        height={viewHeight}
        role="img"
        aria-labelledby={titleId}
        onMouseMove={(event) => onHover(dayUnderPointer(event))}
        onMouseLeave={() => onHover(null)}
      >
        <title id={titleId}>{label}</title>

        {/* La colonna evidenziata: e' la stessa data in tutti i riquadri della
            pagina, ed e' cio' che li rende leggibili in verticale. Si disegna
            anche su un giorno senza punti — il vuoto e' informazione. */}
        {hoveredDate && (
          <line
            className="briefing-history__cursor"
            x1={PADDING_LEFT + dayOffset(hoveredDate, domain.from, domain.to) * plotWidth}
            x2={PADDING_LEFT + dayOffset(hoveredDate, domain.from, domain.to) * plotWidth}
            y1={PADDING_TOP}
            y2={viewHeight - PADDING_BOTTOM}
          />
        )}

        {scene.ticks.map((tick) => (
          <g key={tick.value}>
            <line
              className={`briefing-history__grid${tick.value === 0 && lowerBelowZero(scene.ticks) ? ' briefing-history__grid--zero' : ''}`}
              x1={PADDING_LEFT}
              x2={width - PADDING_RIGHT}
              y1={tick.y}
              y2={tick.y}
            />
            <text className="briefing-history__axis" x={PADDING_LEFT - 8} y={tick.y + 4} textAnchor="end">
              {format(String(tick.value))}
            </text>
          </g>
        ))}

        {scene.lines.map((line) => (
          <g key={line.label} className={`briefing-history__series briefing-history__series--${line.kind}`}>
            <polyline
              className="briefing-history__line"
              fill="none"
              points={line.samples.map((sample) => `${sample.x},${sample.y}`).join(' ')}
            />
            {line.samples.map((sample) => (
              <circle
                key={sample.date}
                className="briefing-history__dot"
                cx={sample.x}
                cy={sample.y}
                r={hoveredDate === sample.date ? 5 : 3}
              >
                <title>{`${line.label} · ${formatDate(sample.date)} · ${format(sample.value)}`}</title>
              </circle>
            ))}
          </g>
        ))}

        {/* Gli estremi dell'asse sono quelli della FINESTRA, non del primo e
            dell'ultimo punto: e' la stessa scala della timeline sopra. */}
        <text className="briefing-history__axis" x={PADDING_LEFT} y={viewHeight - 8} textAnchor="start">
          {formatDate(domain.from)}
        </text>
        <text className="briefing-history__axis" x={width - PADDING_RIGHT} y={viewHeight - 8} textAnchor="end">
          {formatDate(domain.to)}
        </text>
      </svg>

      {hoveredDate && (
        <div className="briefing-history__tooltip" role="status">
          <span className="briefing-history__tooltip-date">{formatDate(hoveredDate)}</span>
          {hovered.length === 0 ? (
            <span className="cell--minor">nessun valore quel giorno</span>
          ) : (
            hovered.map((entry) => (
              <span key={entry.label}>
                {entry.label}: {format(entry.sample.value)}
              </span>
            ))
          )}
        </div>
      )}

      {series.length > 1 && (
        <ul className="briefing-history__legend">
          {series.map((line) => (
            <li key={line.label} className={`briefing-history__legend-item--${line.kind}`}>
              {line.label}
            </li>
          ))}
        </ul>
      )}

      {/* L'informazione non è mai solo nella forma del grafico. */}
      <table className="visually-hidden">
        <caption>{label}</caption>
        <thead>
          <tr>
            <th scope="col">Data</th>
            {scene.lines.map((line) => (
              <th key={line.label} scope="col">
                {line.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {[...new Set(scene.lines.flatMap((line) => line.samples.map((sample) => sample.date)))]
            .sort()
            .map((date) => (
              <tr key={date}>
                <td>{formatDate(date)}</td>
                {scene.lines.map((line) => {
                  const sample = line.samples.find((entry) => entry.date === date);
                  return <td key={line.label}>{sample ? format(sample.value) : '—'}</td>;
                })}
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}
